import { useEffect, useMemo } from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CadastroImobiliariaCorretorForm } from '@/features/proposta/components/cadastro-imobiliaria-corretor-form'
import { CardEtapaContent, CardEtapaItem } from '@/features/proposta/components/ui/card-etapa'
import { CadastroImobiliariaCorretorEntity } from '@/features/proposta/entities/cadastro-imobiliaria-corretor.entity'
import { useCorretagens } from '@/features/proposta/hooks/use-corretagens'
import type { TDetalhesPropostaSchema } from '@/features/proposta/schemas/detalhes-proposta-schema'
import { Render } from '@/shared/components/render-if'
import { Label } from '@/shared/components/ui'
import { FormField, FormItem } from '@/shared/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'

type TImobiliariaCorretorProps = {
  exibeFormaPagamentoComissao?: boolean
  simulacoesIds: string[]
  isStepCompleted: boolean
  percentualCorretagem: number | null
  valorCorretagem: number | null
  handleRadioValueChange: (
    field: { onChange: (value: string) => void },
    entity: CadastroImobiliariaCorretorEntity,
  ) => (value: string) => void
}
const ImobiliariaCorretorStep = ({
  exibeFormaPagamentoComissao,
  simulacoesIds,
  isStepCompleted,
  percentualCorretagem,
  valorCorretagem,
  handleRadioValueChange,
}: TImobiliariaCorretorProps) => {
  const { t } = useTranslation('proposta')
  const { data } = useCorretagens(simulacoesIds)
  const { control, watch, setValue } = useFormContext<TDetalhesPropostaSchema>()

  const cadastroImobiliariaCorretorEntity = useMemo(
    () => new CadastroImobiliariaCorretorEntity(data ?? [], percentualCorretagem, valorCorretagem),
    [data, percentualCorretagem, valorCorretagem],
  )

  const participacoesPath = 'detalhesDaProposta.imobiliariasCorretores.participacoes'
  const isSemParticipacoes = watch('detalhesDaProposta.imobiliariasCorretores.incluirParticipacoes') === 'Não'

  const labelNao = t('detalhes_proposta.imobiliaria.inputs.radio.nao')
  const labelSim = t('detalhes_proposta.imobiliaria.inputs.radio.sim')
  const isAdicionarNovaParticipacaoDesabilitado =
    watch(participacoesPath)?.filter((p) => !p.imobiliariaCorretor.idExterno)?.length > 0

  const isParticipacoesVazias = watch(participacoesPath)?.length === 0
  useEffect(() => {
    if (!isSemParticipacoes && isParticipacoesVazias) {
      setValue('detalhesDaProposta.imobiliariasCorretores.incluirParticipacoes', 'Não')
    }
  }, [isParticipacoesVazias, isSemParticipacoes, setValue])

  return (
    <CardEtapaItem
      titulo={t('detalhes_proposta.imobiliaria.titulo')}
      descricao={t('detalhes_proposta.imobiliaria.descricao')}
      completed={isStepCompleted}
    >
      <CardEtapaContent>
        <span className="text-sm font-medium text-neutral-800">
          {t('detalhes_proposta.imobiliaria.inputs.radio.label')}
        </span>
        <FormField
          control={control}
          name="detalhesDaProposta.imobiliariasCorretores.incluirParticipacoes"
          render={({ field }) => (
            <FormItem>
              <RadioGroup
                className="flex flex-col gap-6"
                value={field.value}
                onValueChange={handleRadioValueChange(field, cadastroImobiliariaCorretorEntity)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={labelSim} value={labelSim} />
                  <Label htmlFor={labelSim} aria-labelledby={labelSim} className="font-nunito text-base font-normal">
                    {labelSim}
                  </Label>
                </div>
                <Render.If condition={field.value === labelSim}>
                  {() => (
                    <CadastroImobiliariaCorretorForm
                      control={control}
                      exibeFormaPagamentoComissao={exibeFormaPagamentoComissao}
                      cadastroImobiliariaCorretoresEntity={cadastroImobiliariaCorretorEntity}
                      formaPagamentoComissao={
                        watch('detalhesDaProposta.imobiliariasCorretores.formaPagamentoComissao') ?? 'PagaPelaPlaenge'
                      }
                      isAdicionarNovaParticipacaoDesabilitado={isAdicionarNovaParticipacaoDesabilitado}
                    />
                  )}
                </Render.If>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={labelNao} value={labelNao} />
                  <Label aria-labelledby={labelNao} className="font-nunito text-base font-normal" htmlFor={labelNao}>
                    {labelNao}
                  </Label>
                </div>
              </RadioGroup>
            </FormItem>
          )}
        />
      </CardEtapaContent>
    </CardEtapaItem>
  )
}

export { ImobiliariaCorretorStep }
